import React from 'react';
import { getEditableStatusOptions, formatStatus } from '../constants/workflowLabels';
import { normalizeAlarmLocation } from '../utils/permitFields';
import FeeFieldsFormSection from './FeeFieldsFormSection';

/**
 * GovOps permit PATCH form (map page, permit details).
 */
const PermitEditPanel = ({
  permit,
  onChange,
  onSave,
  onCancel,
  saving = false,
  saveSuccess = false,
  title = 'Update permit',
}) => {
  if (!permit?.id) return null;

  const coords = permit.coordinates || permit.alarmLocation?.coordinates || {};
  const loc = normalizeAlarmLocation(permit);
  const contact = permit.contact || {};
  const statusOptions = getEditableStatusOptions(permit.status);
  const hasStatusChoices = statusOptions.length > 1;

  const patch = (partial) => onChange({ ...permit, ...partial });

  const patchLoc = (field, value) => {
    const nextLoc = { ...loc, [field]: value };
    patch({
      alarmLocation: { ...(permit.alarmLocation || {}), ...nextLoc },
      [field]: value,
    });
  };

  const patchContact = (field, value) => {
    patch({ contact: { ...contact, [field]: value } });
  };

  return (
    <section className="rhs-card rhs-permit-edit" aria-labelledby="permit-edit-heading">
      <h3 id="permit-edit-heading" className="rhs-card-title">
        {title}
      </h3>
      <div className="rhs-form-block rhs-form-block--compact">
        <label htmlFor="edit-status">Status</label>
        <select
          id="edit-status"
          value={permit.status || 'intake'}
          onChange={(e) => patch({ status: e.target.value })}
          disabled={saving || !hasStatusChoices}
        >
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {formatStatus(s)}
            </option>
          ))}
        </select>
        {!hasStatusChoices && (
          <p className="rhs-muted text-sm">
            Status is {formatStatus(permit.status)} — no further workflow steps from this state.
          </p>
        )}
        {hasStatusChoices && statusOptions.length > 1 && (
          <p className="rhs-muted text-sm">Only valid next workflow steps are shown (GovOps rules).</p>
        )}

        <label htmlFor="edit-applicant">Applicant name</label>
        <input
          id="edit-applicant"
          value={permit.applicantName || ''}
          onChange={(e) => patch({ applicantName: e.target.value })}
          disabled={saving}
        />

        <label htmlFor="edit-phone">Phone</label>
        <input
          id="edit-phone"
          value={permit.phoneNumber || ''}
          onChange={(e) => patch({ phoneNumber: e.target.value })}
          disabled={saving}
        />

        <label htmlFor="edit-type">Permit type</label>
        <input
          id="edit-type"
          value={permit.type || ''}
          onChange={(e) => patch({ type: e.target.value })}
          disabled={saving}
        />

        <label htmlFor="edit-description">Description</label>
        <textarea
          id="edit-description"
          rows={2}
          value={permit.description || ''}
          onChange={(e) => patch({ description: e.target.value })}
          disabled={saving}
        />

        <label htmlFor="edit-permit-number">Permit / reference #</label>
        <input
          id="edit-permit-number"
          value={permit.permitNumber || ''}
          onChange={(e) => patch({ permitNumber: e.target.value })}
          disabled={saving}
        />

        <h4 className="rhs-card-title">Alarm location</h4>
        <label htmlFor="edit-street">Street</label>
        <input
          id="edit-street"
          value={loc.street}
          onChange={(e) => patchLoc('street', e.target.value)}
          disabled={saving}
        />
        <label htmlFor="edit-unit">Unit</label>
        <input
          id="edit-unit"
          value={loc.unit}
          onChange={(e) => patchLoc('unit', e.target.value)}
          disabled={saving}
        />
        <div className="rhs-form-row">
          <div>
            <label htmlFor="edit-city">City</label>
            <input
              id="edit-city"
              value={loc.city}
              onChange={(e) => patchLoc('city', e.target.value)}
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="edit-state">State</label>
            <input
              id="edit-state"
              value={loc.state}
              onChange={(e) => patchLoc('state', e.target.value)}
              disabled={saving}
            />
          </div>
        </div>
        <label htmlFor="edit-zip">Zip code</label>
        <input
          id="edit-zip"
          value={loc.zipCode}
          onChange={(e) => patchLoc('zipCode', e.target.value)}
          disabled={saving}
        />

        <h4 className="rhs-card-title">Contact</h4>
        <label htmlFor="edit-contact-name">Contact name</label>
        <input
          id="edit-contact-name"
          value={contact.name || ''}
          onChange={(e) => patchContact('name', e.target.value)}
          disabled={saving}
        />
        <label htmlFor="edit-contact-email">Email</label>
        <input
          id="edit-contact-email"
          type="email"
          value={contact.email || ''}
          onChange={(e) => patchContact('email', e.target.value)}
          disabled={saving}
        />

        <FeeFieldsFormSection
          values={permit}
          onChange={onChange}
          disabled={saving}
          idPrefix="edit-fee"
        />

        <div className="rhs-form-row">
          <div>
            <label htmlFor="edit-lat">Latitude</label>
            <input
              id="edit-lat"
              type="number"
              step="any"
              value={coords.latitude ?? ''}
              onChange={(e) =>
                patch({
                  coordinates: { ...coords, latitude: e.target.value },
                })
              }
              disabled={saving}
            />
          </div>
          <div>
            <label htmlFor="edit-lng">Longitude</label>
            <input
              id="edit-lng"
              type="number"
              step="any"
              value={coords.longitude ?? ''}
              onChange={(e) =>
                patch({
                  coordinates: { ...coords, longitude: e.target.value },
                })
              }
              disabled={saving}
            />
          </div>
        </div>

        <div className="rhs-toolbar-actions">
          <button type="button" className="rhs-btn" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            className="rhs-btn rhs-btn--secondary"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
        {saveSuccess && <p className="rhs-ok-text">Saved successfully.</p>}
      </div>
    </section>
  );
};

export default PermitEditPanel;
